import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';

declare var google: any;

/**
 * Google Places Autocomplete on a plain text input (no map).
 * When the field has a committed address (from DB or a prior pick), Autocomplete is torn down on focus
 * so Google's widget cannot snap the value back; it is re-attached on blur for the next search.
 */
@Component({
  selector: 'app-places-address-input',
  templateUrl: './places-address-input.component.html',
  styleUrls: ['./places-address-input.component.css']
})
export class PlacesAddressInputComponent implements AfterViewInit, OnDestroy, OnChanges {

  @Input() placeholder = 'Start typing to search for an address';
  @Input() address = '';

  @Output() addressChange = new EventEmitter<string>();
  @Output() placeSelected = new EventEmitter<{ address: string; lat: number; lng: number }>();

  @ViewChild('addressInput') addressInputRef: ElementRef;

  private autocomplete: any;
  private placeListener: any;
  private pacFocusHandler: () => void;

  /** Non-null after initial @Input address or a place_changed — triggers AC teardown on focus so Google cannot revert edits. */
  private lastCommittedAddress: string | null = null;

  private addressFieldFocused = false;
  private blurClearFocusTimer: any;
  private pendingReinitAutocomplete = false;

  constructor(private zone: NgZone, private cdr: ChangeDetectorRef) {}

  onAddressFocus() {
    if (this.blurClearFocusTimer) {
      clearTimeout(this.blurClearFocusTimer);
      this.blurClearFocusTimer = null;
    }
    this.addressFieldFocused = true;

    if (this.autocomplete != null && this.lastCommittedAddress != null) {
      this.destroyAutocomplete();
      this.pendingReinitAutocomplete = true;
    }
  }

  onAddressBlur() {
    this.blurClearFocusTimer = setTimeout(() => {
      this.addressFieldFocused = false;
      this.blurClearFocusTimer = null;

      if (this.pendingReinitAutocomplete && this.addressInputRef && this.canUseGooglePlaces()) {
        this.pendingReinitAutocomplete = false;
        const el = this.addressInputRef.nativeElement;
        const v = el.value;
        this.lastCommittedAddress = v != null && String(v).length > 0 ? String(v) : null;
        this.initAutocomplete(el);
      }
      this.cdr.detectChanges();
    }, 250);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!changes.address || !this.addressInputRef || !this.addressInputRef.nativeElement) {
      return;
    }
    if (this.addressFieldFocused) {
      return;
    }
    const el = this.addressInputRef.nativeElement;
    const next = this.address != null ? String(this.address) : '';
    if (el.value === next) {
      this.lastCommittedAddress = next.length > 0 ? next : null;
      return;
    }
    el.value = next;
    this.lastCommittedAddress = next.length > 0 ? next : null;

    if (this.autocomplete) {
      this.destroyAutocomplete();
    }
    if (this.canUseGooglePlaces() && el.ownerDocument) {
      this.pendingReinitAutocomplete = false;
      this.initAutocomplete(el);
    }
  }

  ngAfterViewInit() {
    const input = this.addressInputRef.nativeElement;
    if (this.address) {
      input.value = this.address;
      this.lastCommittedAddress = String(this.address);
    } else {
      this.lastCommittedAddress = null;
    }
    if (this.canUseGooglePlaces()) {
      this.initAutocomplete(input);
    }
  }

  ngOnDestroy() {
    if (this.blurClearFocusTimer) {
      clearTimeout(this.blurClearFocusTimer);
    }
    this.destroyAutocomplete();
  }

  onInput() {
    const el = this.addressInputRef.nativeElement;
    const v = el.value;
    if (
      this.autocomplete != null &&
      this.lastCommittedAddress != null &&
      v !== this.lastCommittedAddress
    ) {
      this.destroyAutocomplete();
      this.pendingReinitAutocomplete = true;
      this.lastCommittedAddress = null;
    }
    this.addressChange.emit(v);
  }

  private canUseGooglePlaces(): boolean {
    return typeof google !== 'undefined' && !!(google.maps && google.maps.places);
  }

  private destroyAutocomplete() {
    const input = this.addressInputRef && this.addressInputRef.nativeElement;
    if (input && this.pacFocusHandler) {
      input.removeEventListener('focus', this.pacFocusHandler);
      this.pacFocusHandler = null;
    }
    if (this.placeListener && google && google.maps && google.maps.event) {
      google.maps.event.removeListener(this.placeListener);
      this.placeListener = null;
    }
    if (this.autocomplete && google && google.maps && google.maps.event) {
      google.maps.event.clearInstanceListeners(this.autocomplete);
      this.autocomplete = null;
    }
  }

  /**
   * Google often puts Open Location Codes (e.g. 4P52+RXQ) in formatted_address for schools/POIs.
   * Skip those so the payload shows a human-readable name (e.g. Jamaica College) instead.
   */
  private looksLikeOpenLocationCodeLine(s: string): boolean {
    if (!s || !String(s).trim()) {
      return false;
    }
    return /\b[23456789CFGHJMPQRVWX]{4,8}\+[23456789CFGHJMPQRVWX]{2,5}\b/i.test(s);
  }

  /**
   * Prefer a full postal-style line: formatted_address, then what the user typed,
   * then "name, vicinity" — avoid using a single-word establishment name alone.
   */
  private resolvePlaceAddressLabel(place: any, input: HTMLInputElement): string {
    const typed = (input.value || '').trim();
    const formattedRaw = (place.formatted_address || '').trim();
    const formattedOk = formattedRaw && !this.looksLikeOpenLocationCodeLine(formattedRaw);
    if (formattedOk) {
      return formattedRaw;
    }
    const name = (place.name || '').trim();
    const vicinity = (place.vicinity || '').trim();
    const named = name && vicinity ? `${name}, ${vicinity}` : (name || vicinity);
    const candidates = [typed, named].filter(Boolean);
    let best = named || typed;
    for (const c of candidates) {
      if (String(c).length > String(best).length) {
        best = c;
      }
    }
    const wordCount = (s: string) => (s.trim().split(/\s+/).filter(Boolean)).length;
    if (wordCount(String(best)) < 2 && typed && wordCount(typed) >= wordCount(String(best))) {
      best = typed;
    }
    const line = (best || typed || name || formattedRaw || '').trim();
    return line;
  }

  private getOptions(): any {
    const defaultBounds = new google.maps.LatLngBounds(
      new google.maps.LatLng(17.546889258900055, -78.54058735825066),
      new google.maps.LatLng(18.591323377300697, -76.02471821762566)
    );
    return {
      appendTo: 'body',
      bounds: defaultBounds
    };
  }

  private initAutocomplete(input: HTMLInputElement) {
    if (this.autocomplete != null) {
      return;
    }
    if (!this.canUseGooglePlaces()) {
      return;
    }

    this.autocomplete = new google.maps.places.Autocomplete(input, this.getOptions());

    this.pacFocusHandler = () => {
      setTimeout(() => {
        const pacContainer = document.querySelector('.pac-container') as HTMLElement;
        if (pacContainer) {
          const rect = input.getBoundingClientRect();
          pacContainer.style.top = `${rect.bottom + window.scrollY}px`;
          pacContainer.style.left = `${rect.left + window.scrollX}px`;
          pacContainer.style.width = `${rect.width}px`;
          pacContainer.style.zIndex = '10050';
        }
      }, 0);
    };
    input.addEventListener('focus', this.pacFocusHandler);

    this.placeListener = this.autocomplete.addListener('place_changed', () => {
      const place = this.autocomplete.getPlace();
      if (!place || !place.geometry) {
        return;
      }
      const lat = place.geometry.location.lat();
      const lng = place.geometry.location.lng();
      const addr = this.resolvePlaceAddressLabel(place, input);
      this.zone.run(() => {
        if (this.blurClearFocusTimer) {
          clearTimeout(this.blurClearFocusTimer);
          this.blurClearFocusTimer = null;
        }
        this.addressFieldFocused = true;
        input.value = addr;
        this.lastCommittedAddress = addr;
        this.addressChange.emit(addr);
        this.placeSelected.emit({ address: addr, lat, lng });
        this.blurClearFocusTimer = setTimeout(() => {
          this.addressFieldFocused = false;
          this.blurClearFocusTimer = null;
        }, 250);
        this.cdr.detectChanges();
      });
    });
  }
}



// WEBPACK FOOTER //
// ./src/app/shared/places-address-input/places-address-input.component.ts